import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAllUser } from '@/api/Admin/User';
import { ListUserAPI } from '@/api/Admin/type';
import { RoleModal } from '@/components/Account/RoleModal';
import EyeIcon from '@/components/Icon/EyeIcon';
import VerticalIcon from '@/components/Icon/VerticalIcon';
import TrashIcon from '@/components/Icon/TrashIcon';
import { toast } from 'react-toastify';

const ListUserPage: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [selectedRoleUserId, setSelectedRoleUserId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const loadUsers = async (page: number, limit: number) => {
    setLoading(true);
    try {
      const response: ListUserAPI = await fetchAllUser(page, limit);
      if (response.status) {
        setUsers(response.data.items);
        setTotal(response.data.total);
      } else {
        toast.error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('An error occurred while fetching users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(currentPage, pageSize);
  }, [currentPage, pageSize]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDelete = (userId: number) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      // Implement delete API call here when available
      toast.success('User deleted successfully (mock)');
      loadUsers(currentPage, pageSize);
    }
  };

  const menuItems = (record: any) => [
    {
      label: 'Edit Profile',
      onClick: () => navigate(`/admin/accounts/edit/${record.id}`),
    },
    {
      label: 'View Transactions',
      onClick: () => navigate(`/admin/investors/${record.id}?tab=transactions`),
    },
    {
      label: 'View Packages',
      onClick: () => navigate(`/admin/investors/${record.id}?tab=investment`),
    },
    {
      label: 'Add Package',
      onClick: () => navigate(`/admin/users/register-package?user_id=${record.id}`),
    },
    {
      label: 'Change Role',
      onClick: () => setSelectedRoleUserId(String(record.id)),
    },
  ];

  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      <div className="p-6 bg-white dark:bg-neutral-900 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-800">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-neutral-100">Accounts List</h2>
            <p className="text-sm text-gray-500 dark:text-neutral-400 mt-1">Total {total} users found</p>
          </div>
          <button
            onClick={() => navigate('/admin/accounts/create')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center shadow-sm"
          >
            <span className="mr-2 text-lg">+</span> New Account
          </button>
        </div>

        <div className="overflow-x-auto relative">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-neutral-800/50 border-b border-gray-200 dark:border-neutral-800">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider w-20">ID</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Full Name</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Role</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-neutral-800">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500 dark:text-neutral-400">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <span>Loading users...</span>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500 dark:text-neutral-400">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-neutral-800/30 transition-colors duration-150">
                    <td className="px-4 py-4 text-sm text-gray-600 dark:text-neutral-400">{user.id}</td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-800 dark:text-neutral-200">{user.fullname}</td>
                    <td className="px-4 py-4 text-sm text-gray-600 dark:text-neutral-400">{user.email}</td>
                    <td className="px-4 py-4 text-sm text-gray-600 dark:text-neutral-400">{user.phone}</td>
                    <td className="px-4 py-4">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full border border-blue-100 dark:border-blue-900/30">
                        {user.roles_display || 'Member'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${user.is_active === 1
                          ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-100 dark:border-green-900/30'
                          : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-100 dark:border-red-900/30'
                        }`}>
                        {user.is_active === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex justify-end items-center gap-2">
                        <button
                          onClick={() => navigate(`/admin/accounts/view/${user.id}`)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 dark:text-neutral-500 dark:hover:text-blue-400 transition-colors"
                          title="View Details"
                        >
                          <EyeIcon />
                        </button>

                        <div className="relative" ref={openDropdownId === user.id ? dropdownRef : null}>
                          <button
                            onClick={() => setOpenDropdownId(openDropdownId === user.id ? null : user.id)}
                            className={`p-1.5 text-gray-400 dark:text-neutral-500 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded transition-colors ${openDropdownId === user.id ? 'bg-gray-100 dark:bg-neutral-800' : ''}`}
                          >
                            <VerticalIcon />
                          </button>

                          {openDropdownId === user.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-neutral-800 rounded-lg shadow-xl border border-gray-200 dark:border-neutral-700 z-50 py-1 overflow-hidden animate-in fade-in zoom-in duration-100">
                              {menuItems(user).map((item, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => {
                                    item.onClick();
                                    setOpenDropdownId(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-neutral-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                >
                                  {item.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => handleDelete(user.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 dark:text-neutral-500 dark:hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-200 dark:border-neutral-800">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 dark:text-neutral-400">Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-700 text-gray-700 dark:text-neutral-300 text-sm rounded-md px-2 py-1 outline-none focus:ring-1 focus:ring-blue-500 transition-all"
            >
              {[5, 10, 20, 50].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || loading}
              className="p-2 border border-gray-300 dark:border-neutral-700 rounded-md hover:bg-gray-100 dark:hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-neutral-400 transition-colors"
            >
              &lt;
            </button>
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">Page {currentPage} of {totalPages}</span>
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages || loading}
              className="p-2 border border-gray-300 dark:border-neutral-700 rounded-md hover:bg-gray-100 dark:hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-neutral-400 transition-colors"
            >
              &gt;
            </button>
          </div>
        </div>
      </div>

      <RoleModal
        isOpen={selectedRoleUserId !== null}
        user={users.find(user => String(user.id) === selectedRoleUserId) || { id: '', email: '', name: '', role: '' }}
        onClose={() => {
          setSelectedRoleUserId(null);
          loadUsers(currentPage, pageSize);
        }}
      />
    </>
  );
};

export default ListUserPage;
